<script>
import { mapState } from 'vuex';
import isFunction from 'lodash/isFunction';
import flatPickr from 'vue-flatpickr-component';
import QuotePositionModel from '@models/QuotePosition';
import QuoteModel from '@models/Quote';
import ParamModel from '@models/Param';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import datePickerUtility from '@mixins/datePickerUtility';
import utility from '@mixins/utility';
import QuotePosition from '@/src/models/QuotePosition';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  components: {
    BlockingContainer,
    flatPickr,
    FontAwesomeIcon,
  },
  mixins: [editFormChange, datePickerUtility, utility],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    quote: {
      required: true,
      default: null,
    },
  },
  data() {
    return {
      showPriceListModal: false,
      descriptions: [],
      isCustomDescription: false,
      quotePosition: {
        is_info: 0,
        date: null,
        description: null,
        description_id: null,
        remark: null,
        price: 0,
        amount: 0,
      },
      defaultQuotePosition: {
        is_info: 0,
        date: null,
        description: null,
        description_id: null,
        remark: null,
        price: 0,
        amount: 0,
      },
    };
  },
  computed: {
    ...mapState({
      dateOnlyConfig: (state) => state.common.dateOnlyConfig,
    }),
    priceDisplay: {
      get() {
        return this.quotePosition.price ? String(this.quotePosition.price).replace('.', ',') : '';
      },
      set(value) {
        // Allow both comma and dot, convert to dot for storage
        this.quotePosition.price = value.replace(',', '.');
      },
    },
  },
  mounted() {

    // quote must be present here
    if(this.quote === null) {
      return;
    }

    // fetch areas
    (new ParamModel).params({
          select_many: ['quote_position_descriptions'].join(',')
        }).first()
    .then((response) => {
      this.descriptions = response.quote_position_descriptions || [];
    });

    if (this.loadedId) {
      this.$wait.start('quote-position loading');
      new QuoteModel({ id: this.quote.id })
        .quotePositions()
        .find(this.loadedId)
        .then((response) => {
          this.quotePosition = Object.assign({}, this.quotePosition, response.quote_position);

          // Set isCustomDescription flag based on loaded data
          if (this.quotePosition.description && !this.quotePosition.description_id) {
            this.isCustomDescription = true;
          }

          this.$wait.end('quote-position loading');
          this.$nextTick(() => {
            this.formatPrice();
            // Use setTimeout to ensure all reactive updates have settled
            setTimeout(() => {
              this.initChangeWatcher(['quotePosition'], this.validate);
            }, 300);
          });
        });
    }
  },
  methods: {
    onDescriptionTypeChange(value) {
      if (value === 'custom') {
        // Switch to custom description
        this.isCustomDescription = true;
        this.quotePosition.description_id = null;
        // Only clear description if switching from preset to custom
        if (!this.quotePosition.description) {
          this.quotePosition.description = '';
        }
      } else if (value) {
        // Selected from dropdown
        this.isCustomDescription = false;
        this.quotePosition.description_id = value;
        this.quotePosition.description = null;
      } else {
        // Nothing selected
        this.isCustomDescription = false;
        this.quotePosition.description_id = null;
        this.quotePosition.description = null;
      }
    },
    onIsInfoChange() {
      // Reset amount and price when toggling is_info
      if (this.quotePosition.is_info) {
        this.quotePosition.amount = 0;
      }
    },
    formatPrice() {
      const val = parseFloat(this.quotePosition.price);
      if (!isNaN(val)) {
        this.quotePosition.price = val.toFixed(2);
      }
    },
    resetValidation() {
      this.$validator.reset();
    },
    validate(callback) {
      this.$validator.validateAll('quote-position-form').then((result) => {
        if (result) {
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      const quote = new QuoteModel({ id: this.quote.id });
      this.quotePosition.quote = quote;
      new QuotePositionModel(this.quotePosition)
        .for(quote)
        .save()
        .then((response) => {
          this.quotePosition = Object.assign({}, this.defaultQuotePosition);
          this.$validator.reset();
          this.$emit('submitted', response.quote_position);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('quote-position loading');
        })
        .catch(() => this.$wait.end('quote-position loading'));
    },
    async deleteQuotePosition() {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.quote_position') }))) {
        this.$wait.start(`quote_position${this.quotePosition.id}`);
        const quotePosition = new QuotePosition({ id: this.quotePosition.id });
        const quote = new QuoteModel({ id: this.quote.id });
        await quotePosition.for(quote).delete();
        this.$emit('deleted');
        this.$wait.end(`quote_position${this.quotePosition.id}`);
      }
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('quote-position loading')"></BlockingContainer>
    <b-form data-vv-scope="quote-position-form" @submit.prevent="validate">
      <b-form-row>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.date') }}:
            </template>
            <flat-pickr
              ref="serviceStart"
              v-model="quotePosition.date"
              v-validate="''"
              data-vv-name="date"
              :data-vv-as="$t('fields.date')"
              aria-describedby="input-feedback-date"
              :config="dateOnlyConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('date', 'quote-position-form') }"
              :placeholder="$t('placeholders.date')"
              @on-close="closeCalendar('quotePosition.date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
            >
            </flat-pickr>
            <b-form-invalid-feedback id="input-feedback-date">
              <template v-show="errors.has('date', 'quote-position-form')"
                >{{ errors.first('date', 'quote-position-form') }}
              </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-group v-if="!isCustomDescription">
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.description') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-select
          v-model="quotePosition.description_id"
          :v-validate="!isCustomDescription ? 'required' : ''"
          data-vv-value-path="localValue"
          data-vv-name="description"
          :data-vv-as="$t('fields.description')"
          :state="errors.has('description', 'quote-position-form') ? false : null"
          aria-describedby="input-feedback-description"
          @input="onDescriptionTypeChange"
        >
          <option :value="null">{{ $t('placeholders.select_description') }}</option>
          <option value="custom">
            <b>{{ $t('fields.custom_description') }}</b>
          </option>
          <option disabled>──────────</option>
          <option v-for="(description, index) in descriptions" :key="`descriptionSelect${index}`" :value="description.id">
            {{ quote.language == 'en' ? description.description_en : description.description_de }}
          </option>
        </b-form-select>
        <b-form-invalid-feedback id="input-feedback-description">
          <template v-show="errors.has('description', 'quote-position-form')"
            >{{ errors.first('description', 'quote-position-form') }}
          </template>
        </b-form-invalid-feedback>
      </b-form-group>
      <!-- Custom Description Input -->
      <b-form-group v-else>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.custom_description') }}:
          <span class="status-star text-danger">*</span>
          <b-button
            variant="link"
            size="sm"
            class="p-0 ml-2"
            @click="onDescriptionTypeChange(null)"
          >
            {{ $t('actions.select_from_list') }}
          </b-button>
        </template>
        <b-form-input
          v-model="quotePosition.description"
          :v-validate="isCustomDescription ? 'required|max:500' : ''"
          data-vv-value-path="localValue"
          data-vv-name="description"
          :data-vv-as="$t('fields.description')"
          type="text"
          :state="errors.has('description', 'quote-position-form') ? false : null"
          aria-describedby="input-feedback-description"
          :placeholder="$t('placeholders.enter_custom_description')"
        >
        </b-form-input>
        <b-form-invalid-feedback id="input-feedback-description">
          <template v-show="errors.has('description', 'quote-position-form')"
            >{{ errors.first('description', 'quote-position-form') }}
          </template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-row>
        <b-col sm="12">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.remark') }}: </template>
            <b-form-textarea
              id="remark"
              v-model="quotePosition.remark"
              v-validate="'max:65000'"
              data-vv-value-path="localValue"
              data-vv-name="remark"
              :rows="1"
              :state="errors.has('remark', 'quote-position-form') ? false : null"
              aria-describedby="input-feedback-remark"
              class="single-line-textarea"
            >
            </b-form-textarea>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-group :label="`${$t('fields.just_info')}:`">
        <b-form-checkbox v-model="quotePosition.is_info" :value="1" :unchecked-value="0" switch @change="onIsInfoChange">&nbsp;</b-form-checkbox>
      </b-form-group>
      <template v-if="!quotePosition.is_info">
        <b-form-group>
          <template v-slot:label class="col-form-legend col-12">
            {{ $t('fields.just_amount') }}:
            <span class="status-star text-danger">*</span>
          </template>
          <b-form-input
            id="amount"
            v-model="quotePosition.amount"
            v-validate="'required|numeric|min_value:0'"
            data-vv-value-path="localValue"
            data-vv-name="amount"
            :data-vv-as="$t('fields.amount')"
            type="number"
            :state="errors.has('amount', 'quote-position-form') ? false : null"
            aria-describedby="input-feedback-amount"
            :placeholder="$t('placeholders.amount')"
          >
          </b-form-input>
          <b-form-invalid-feedback id="input-feedback-amount">
            <template v-show="errors.has('amount', 'quote-position-form')">
              {{ errors.first('amount', 'quote-position-form') }}
            </template>
          </b-form-invalid-feedback>
        </b-form-group>
      </template>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('table_labels.unit_price') }}:
          <span class="status-star text-danger">*</span>
          <FontAwesomeIcon :icon="['fas', 'file']" class="text-secondary cursor-pointer ml-2" @click="showPriceListModal = true"/>
        </template>
        <b-form-input
          id="price"
          v-model="priceDisplay"
          v-validate="'required'"
          data-vv-value-path="localValue"
          data-vv-name="price"
          :data-vv-as="$t('fields.price')"
          type="text"
          :state="errors.has('price', 'quote-position-form') ? false : null"
          aria-describedby="input-feedback-price"
          :placeholder="$t('placeholders.price')"
          @blur="formatPrice"
        >
        </b-form-input>
        <b-form-invalid-feedback id="input-feedback-price">
          <template v-show="errors.has('price', 'quote-position-form')">
            {{ errors.first('price', 'quote-position-form') }}
          </template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-row class="mb-3">
        <b-col>
          <b-button
            ref="quotePositionSubmitBtn"
            type="submit"
            :disabled="errors.any('quote-position-form')"
            :variant="'outline-' + (!errors.any('quote-position-form') ? 'dark' : 'danger')"
          >
            {{ $t('actions.submit') }}
          </b-button>
          <b-button v-if="loadedId" ref="quotePositionDeleteBtn" variant="outline-danger" class="ml-2" @click="deleteQuotePosition">
            {{ $t('actions.delete') }}
          </b-button>
        </b-col>
      </b-form-row>
    </b-form>
    <b-modal
      ref="pricelist-modal"
      v-model="showPriceListModal"
      resizable
      hide-footer
      scrollable
      centered
      dialog-class="custom-modal-dialog"
      :title="$t('titles.price_list')"
    >
      <b-img :src="require('@assets/images/KKS_Pricelist.png')" fluid alt="Price List" />
    </b-modal>
  </div>
</template>
<style>
.custom-modal-dialog {
  width: auto;
  max-width: 100%;
}

.custom-modal-dialog .modal-content{
  padding: 0;
  width: auto;
}

.custom-modal-dialog .modal-body {
    padding: 0;
}
</style>
<style lang="css" scoped>
>>> .executor-radio-group label {
  width: 100%;
}

.btn-group-toggle .btn {
  flex: 1;
  min-width: 150px;
}

.form-check-inline .btn,
.btn-group .btn-outline-secondary {
  flex: 1;
  min-width: 150px;
}

.input-group-append .btn {
  min-width: 80px;
}

.input-group-append .btn-secondary,
.input-group-append .btn-outline-secondary {
  min-width: auto;
}

.input-group-append .btn-dark {
  min-width: 80px;
}

/* Make textarea start at input field height but allow expansion */
.single-line-textarea {
  min-height: calc(1.5em + 0.75rem + 2px);
  height: calc(1.5em + 0.75rem + 2px);
  resize: vertical;
  line-height: 1.5;
  overflow-y: auto;
}

/* Allow natural expansion when typing */
.single-line-textarea:focus {
  height: auto;
  min-height: calc(1.5em + 0.75rem + 2px);
}
</style>
